import { EMAIL_STATUSES, type EmailStatus } from '@atlas-control/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';
import { PageHeader } from '@/components/page-header';
import { Pagination } from '@/components/pagination';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { outboxQuery } from '@/features/email/api';
import { EmailStatusBadge } from '@/features/email/badges';
import { formatDateTime } from '@/lib/format';
import { guard } from '@/lib/guards';
import { pageSearch } from '@/lib/page-search';

export const Route = createFileRoute('/_app/emails')({
  beforeLoad: ({ context }) => guard(context.me, 'runs.read'),
  validateSearch: z.object({ status: z.enum(EMAIL_STATUSES).optional(), page: pageSearch }),
  component: EmailsPage,
});

/** Every client email across runs. Block, schedule and send are on each run's Email tab. */
function EmailsPage() {
  const { status, page = 1 } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const list = useQuery(outboxQuery(status, page));
  const setPage = useCallback(
    (n: number) => void navigate({ search: (p) => ({ ...p, page: n > 1 ? n : undefined }) }),
    [navigate],
  );

  return (
    <>
      <PageHeader title="Emails" />
      <Tabs
        className="mb-4"
        value={status ?? 'all'}
        onValueChange={(v) =>
          void navigate({ search: { status: v === 'all' ? undefined : (v as EmailStatus) } })
        }
      >
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
          <TabsTrigger value="blocked">Blocked</TabsTrigger>
          <TabsTrigger value="failed">Failed</TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
        </TabsList>
      </Tabs>
      <Card className="py-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>To</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Not before</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead>Last error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.isPending && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Skeleton className="h-5" />
                </TableCell>
              </TableRow>
            )}
            {list.data?.emails.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  No emails here.
                </TableCell>
              </TableRow>
            )}
            {list.data?.emails.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="font-medium">
                  <Link to="/runs/$runId/email" params={{ runId: e.runId }}>
                    {e.company}
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground">{e.toEmail ?? '-'}</TableCell>
                <TableCell>
                  <EmailStatusBadge status={e.status} />
                </TableCell>
                <TableCell>{e.sendAfter ? formatDateTime(e.sendAfter) : 'When ready'}</TableCell>
                <TableCell>{formatDateTime(e.sentAt)}</TableCell>
                <TableCell className="max-w-72 truncate text-danger">{e.lastError ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Pagination info={list.data} onPageChange={setPage} />
    </>
  );
}
